import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Events from '@activebrands/core-web/libs/Events';
import { CheckoutEvents } from '@activebrands/core-web/libs/Events/types';
import { CheckoutTracking } from '@activebrands/core-web/libs/Tracking/constants';
import { styled } from '@activebrands/core-web/libs/styletron';
import { selectShippingMethod } from '@activebrands/core-web/state/basket/actions';
import { isValidBasketAddress } from '@activebrands/core-web/utils/validation';
import RadioInput from 'components/inputs/RadioInput';

const Form = styled('form', {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px 0',
});

const DefaultShipping = ({ onLoad = () => {}, eventCallback = () => {} }) => {
    const dispatch = useDispatch();

    const { shippingMethods = {}, shippingMethodId, currency, address } = useSelector(state => state.basket);
    const shippingOptions = Object.values(shippingMethods).filter(method => method.id !== 'ingrid');
    const [selected, setSelected] = useState(shippingMethodId);
    const prevOptionIds = useRef(shippingOptions.map(o => o.id).join());

    useEffect(() => {
        // Make sure we have the cheapest shipping method, in case there are more than one shipping method...
        if (
            shippingOptions.length > 1 &&
            (prevOptionIds.current !== shippingOptions.map(o => o.id).join() || !isValidBasketAddress(address))
        ) {
            prevOptionIds.current = shippingOptions.map(o => o.id).join();
            shippingOptions.sort((a, b) => a.priceAsNumber - b.priceAsNumber);

            if (selected !== shippingOptions[0].id) {
                setSelected(shippingOptions[0].id);
                dispatch(selectShippingMethod(shippingOptions[0].id));
            }
            Events.trigger('shipping.loaded');
        } else if (shippingOptions.length === 1 && selected !== shippingOptions[0].id) {
            setSelected(shippingOptions[0].id);
        }
    }, [shippingOptions]);

    useEffect(() => {
        onLoad && onLoad();
        if (shippingOptions.length === 1) {
            eventCallback('data_changed');
            dispatch(selectShippingMethod(shippingOptions[0].id));
        } else if (shippingOptions.length > 1) {
            eventCallback('data_changed');
            dispatch(selectShippingMethod(selected));
        } else {
            eventCallback('no_shipping_options');
        }
        // Handle so the customer can buy the product even if the ingrid widget
        // didnt find a correct pick up point and we fallback to default
        Events.trigger(CheckoutEvents.OPTION, {
            step: CheckoutTracking.SHIPPING_STEP,
            option: shippingMethodId,
            currencyCode: currency,
        });
    }, [shippingMethodId]);

    const handleChange = e => {
        setSelected(e.target.id);
        dispatch(selectShippingMethod(e.target.id));
    };

    return (
        <Form onChange={handleChange}>
            {shippingOptions.map(method => (
                <RadioInput
                    readOnly
                    checked={selected === method.id}
                    id={method.id}
                    key={method.id}
                    label={`${method.name} (${method.price})`}
                    name="shippingMethodId"
                    tabIndex={shippingOptions.length > 1 ? '0' : '-1'}
                    theme="default"
                    type="radio"
                />
            ))}
        </Form>
    );
};

DefaultShipping.propTypes = {
    eventCallback: PropTypes.func.isRequired,
    onLoad: PropTypes.func.isRequired,
};

export default DefaultShipping;
